import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Typography,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Box, Stack, Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';

// Build a CSV string from columns [{id,label}] and plain-object rows, then download it.
function downloadCSV(filename, columns, rows) {
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = columns.map((c) => esc(c.label)).join(',');
  const body = rows.map((r) => columns.map((c) => esc(r[c.id])).join(',')).join('\n');
  const csv = `${header}\n${body}`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// A drill-down report: a title, a data table, and a CSV export. Opened by clicking
// any insight card or KPI tile so the derived rows can be reviewed and exported.
const DrillDialog = ({ open, onClose, title, subtitle, columns = [], rows = [] }) => {
  const filename = `${(title || 'report').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${new Date().toISOString().slice(0, 10)}.csv`;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>{title}</Typography>
          <Chip size="small" variant="outlined" label={`${rows.length} rows`} />
        </Stack>
        {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {rows.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">No rows for this selection.</Typography>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 460 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((c) => <TableCell key={c.id} align={c.align || 'left'}>{c.label}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={i} hover>
                    {columns.map((c) => (
                      <TableCell key={c.id} align={c.align || 'left'}
                        sx={c.mono ? { fontFamily: 'monospace' } : undefined}>
                        {r[c.id]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" startIcon={<DownloadOutlinedIcon />}
          disabled={rows.length === 0}
          onClick={() => downloadCSV(filename, columns, rows)}>
          Export CSV
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DrillDialog;
